import React, { FC, MutableRefObject, ReactElement, ReactNode, useContext, useEffect, useState } from 'react'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import useIsMountedRef from 'use-is-mounted-ref'
import { Paper } from '@material-ui/core'
import { DocumentViewer as DocumentViewerLanguage } from '../../../../../i18n'
import { ReactState } from '../../../../React/Common/Types/Types'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { getAkixiDocumentEntry } from '../../../../React/Common/API/Contentful/ContentfulAPI'
import { Tags } from '../../../../React/Common/API/Contentful/Constants'
import { AkixiDocumentAsset, AkixiDocumentEntry, AssetEntryMap } from '../../../../React/Common/API/Contentful/Types'
import { WYSIWYGDisplay } from '../../../../React/Common/Components/WYSIWYGDisplay/WYSIWYGDisplay'
import Loader from '../../../../React/Common/Components/Loader'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { performAuthOutflowRouting } from '../../../Utilities/RoutingUtils'

type Props = {
    tag: Tags
    bottomLeft?: ReactElement | ReactElement[]
    bottomRight?: ReactElement | ReactElement[] | ReactNode
    showDefaultCloseButton?: boolean
}

export const DocumentViewer: FC<Props> = ({
    tag,
    bottomRight,
    bottomLeft,
    showDefaultCloseButton = true
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(DocumentViewerLanguage.NAME)

    const navigate: NavigateFunction = useNavigate()
    const location: Location = useLocation()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [assets, setAssets]: ReactState<Definable<AssetEntryMap>> = useState<Definable<AssetEntryMap>>()
    const [document, setDocument]: ReactState<Definable<AkixiDocumentEntry>> = useState<Definable<AkixiDocumentEntry>>()

    const moveToNextLocation = (): void => performAuthOutflowRouting(navigate, location)

    useEffect((): void => {
        getAkixiDocumentEntry(tag)
            .then((akixiDoc: Definable<AkixiDocumentAsset>): void => {
                if (isMounted.current) {
                    setAssets(akixiDoc?.assets)
                    setDocument(akixiDoc?.entry)
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsLoading(false)
                }
            })
    }, [])

    return (
        <div className='document-viewer'>
            {isStringNotEmpty(document?.title) || isStringNotEmpty(document?.subtitle) ? (
                <div className='dv-title-area'>
                    {document?.title ? <h1>{document?.title}</h1> : <></>}
                    {document?.subtitle ? <h3>{document?.subtitle}</h3> : <></>}
                </div>
            ) : (
                <></>
            )}
            {isLoading ? (
                <div className='router-loader-container'>
                    <Loader />
                </div>
            ) : (
                <Paper elevation={3}>
                    {isVarNotEmpty(document) ? (
                        <WYSIWYGDisplay className='dv-container' content={document.content} assets={assets} />
                    ) : (
                        <></>
                    )}
                    <div className='dv-accept-area'>
                        <div className='dvaa-left'>{bottomLeft}</div>
                        <div className='dvaa-right'>
                            {bottomRight}
                            {showDefaultCloseButton && (
                                <RoundedButton className='dvaa-close-button' onClick={moveToNextLocation}>
                                    {language.get(DocumentViewerLanguage.DocumentViewer_CloseButton)}
                                </RoundedButton>
                            )}
                        </div>
                    </div>
                </Paper>
            )}
        </div>
    )
}
